import * as z from "zod/v4";

/**
 * Admin-only opt-in toggle that wires up the support-v2 infrastructure
 * required for AI replies to inbound SMS on the merchant's Marketing /
 * Transactional phones (campaign and automation replies).
 *
 * Behavior:
 *   - Verifies the team already has `settings.support.enabled === true`. The
 *     admin must enable Support up front; this RPC will refuse to flip
 *     Support on under the hood so admins don't accidentally provision the
 *     support inbox without realizing it.
 *   - Flips `shopperAgent.enabled = true` (the gate at the marketing inbound
 *     edge) via `updateBaseSettings`, which also handles `activePeriods`
 *     bookkeeping.
 *   - Runs the shared `runShopperAgentEnableSideEffects` recipe (also
 *     invoked by the merchant-facing text-concierge onboarding RPC) with
 *     `productBundle: "marketing"`, which:
 *       - Enables `support.ai.enabled`, `support.ai.enabledPlatforms.sms`,
 *         plus other AI flags. The `"marketing"` bundle skips the implicit
 *         Support AI trial grant that text concierge gets, because Marketing
 *         pays for AI replies via its own SKU.
 *       - Writes the CRDB `default-send-ai-response-on-customer-reply` rule
 *         row with `enabled=true, channels=["sms"]` so the support-v2 rule
 *         engine fires the AI agent on inbound SMS.
 *       - Sets `shopperAgent.replyConfig.enabled = true`.
 *
 * The merchant also needs to be on support-v2 (SUPPORT_HELPDESK_V2 feature
 * flag) for replies to actually fire — that gate lives in the rule action
 * and is not flipped by this RPC. Admins should verify the flag is on for
 * the team before relying on AI replies.
 */
export const enableMarketingAiReplies = {
  name: "enableMarketingAiReplies" as const,
  input: z.object({ teamId: z.string() }),
  output: z.object({ success: z.literal(true) }),
};
